import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { passwordPolicyError, PASSWORD_HELP_TEXT } from "../lib/passwordPolicy";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, KeyRound, RefreshCw } from "lucide-react";

// Verificação por Código, item 8/9 — recuperação de senha reaproveita a
// MESMA infraestrutura de OTP do primeiro acesso (purpose="password_reset"),
// em vez de manter um segundo mecanismo (o antigo link por e-mail) em
// paralelo. 3 passos num wizard só: e-mail -> código -> nova senha. Página
// pré-login (mesma família visual de Login/ResetPassword — sem branding do
// tenant, ver decisões documentadas no relatório da Verificação por
// Código); o e-mail mascarado mostrado no passo 2 é o que o PRÓPRIO usuário
// digitou no passo 1 (nunca algo vindo do backend, que precisa continuar
// neutro sobre a conta existir ou não).
const maskEmail = (email) => {
  const [local, domain] = String(email || "").split("@");
  if (!domain) return email || "";
  return `${(local[0] || "*")}***@${domain}`;
};

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1=email, 2=codigo, 3=nova senha
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const submitEmail = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message || "Se os dados informados estiverem cadastrados, enviaremos as instruções para o e-mail associado.");
      setCooldown(60);
      setStep(2);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  const reenviar = async () => {
    if (cooldown > 0 || busy) return;
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setCooldown(60);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setErr("");
    if (!/^\d{6}$/.test(code)) { setErr("Informe os 6 dígitos do código."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-reset/verify-code", { email, code });
      setResetToken(data.token);
      setStep(3);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  const submitNovaSenha = async (e) => {
    e.preventDefault();
    setErr("");
    const pwErr = passwordPolicyError(pw);
    if (pwErr) { setErr(pwErr); return; }
    if (pw !== pw2) { setErr("As senhas não coincidem"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: resetToken, new_password: pw });
      nav("/login", { replace: true, state: { resetOk: true } });
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-xl p-8 shadow-sm" data-testid="forgot-form">
        <Link to="/login" className="text-xs text-blue-600 hover:underline">← Voltar ao login</Link>

        {step === 1 && (
          <form onSubmit={submitEmail}>
            <h1 className="mt-4 font-heading text-2xl font-bold text-slate-900">Recuperar senha</h1>
            <p className="text-slate-500 text-sm mt-1">Informe seu e-mail e enviaremos um código de confirmação.</p>
            <div className="mt-6 space-y-4">
              <div>
                <Label>E-mail</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="forgot-email" />
              </div>
              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
              <Button className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="forgot-submit">
                <Mail size={16} /> {busy ? "Enviando..." : "Enviar código"}
              </Button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitCode}>
            <h1 className="mt-4 font-heading text-2xl font-bold text-slate-900">Verifique seu e-mail</h1>
            <p className="text-slate-500 text-sm mt-1" data-testid="forgot-msg">
              {msg} Verifique também a caixa de spam. Código enviado para <span className="font-medium text-slate-900">{maskEmail(email)}</span>, se cadastrado.
            </p>
            <div className="mt-6 space-y-4">
              <div>
                <Label>Código de 6 dígitos</Label>
                <Input
                  inputMode="numeric" pattern="\d{6}" maxLength={6}
                  value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required autoFocus className="text-center text-2xl tracking-[0.5em]" placeholder="000000"
                  data-testid="forgot-code"
                />
              </div>
              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
              <Button className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="forgot-code-submit">
                <KeyRound size={16} /> {busy ? "Confirmando..." : "Confirmar código"}
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={reenviar} disabled={cooldown > 0 || busy} className="text-blue-600 hover:underline disabled:text-slate-300 disabled:no-underline flex items-center gap-1" data-testid="forgot-reenviar">
                  <RefreshCw size={13} /> {cooldown > 0 ? `Reenviar código (${cooldown}s)` : "Reenviar código"}
                </button>
                <button type="button" onClick={() => { setStep(1); setCode(""); setErr(""); }} className="text-slate-400 hover:text-slate-600">Trocar e-mail</button>
              </div>
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={submitNovaSenha}>
            <h1 className="mt-4 font-heading text-2xl font-bold text-slate-900">Nova senha</h1>
            <p className="text-slate-500 text-sm mt-1">Código confirmado. Escolha uma senha nova para sua conta.</p>
            <div className="mt-6 space-y-4">
              <div>
                <Label>Nova senha</Label>
                <Input type="password" value={pw} onChange={e => setPw(e.target.value)} required data-testid="forgot-pw" />
                <p className="text-xs text-slate-400 mt-1">{PASSWORD_HELP_TEXT}</p>
              </div>
              <div><Label>Confirmar senha</Label><Input type="password" value={pw2} onChange={e => setPw2(e.target.value)} required data-testid="forgot-pw2" /></div>
              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
              <Button className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="forgot-pw-submit">
                <KeyRound size={16} /> {busy ? "Salvando..." : "Redefinir senha"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
